import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { NotesService } from 'src/app/api/notes.service';
import { CustomerLogForStaff } from 'src/app/api/types/partner';
import { Categories, Note, TripDetail } from 'src/app/api/commonInterfaces';
import { NoteCategoryPipe } from 'src/app/component-modules/notes/pipes/note-category.pipe';
import { NoteTypePipe } from 'src/app/component-modules/notes/pipes/note-type.pipe';
import { DatePipe } from '@angular/common';
import { LocalAuthService } from 'src/app/auth/local-auth.service';
import { RouterModule } from '@angular/router';
import { UiModule } from 'src/app/ui/ui.module';
import { of, Subscription } from 'rxjs';
import { Contact } from 'src/app/api/types/contacts';
import { EditScheduledActivityComponentInputs } from 'src/app/component-modules/notes/edit-scheduled-activity/edit-scheduled-activity.component';
import { DataPadService } from 'src/app/ui/data-pad/data-pad.service';
import { IconService } from 'src/app/ui/icon.service';
import { Column, TableColumnsComponent, TableHeadersComponent } from 'src/app/ui/tables';
import { IconButtonComponent } from 'src/app/ui/icon-button/icon-button.component';
import { MessageService } from 'primeng/api';
import { ErrorService } from 'src/app/services/error.service';

interface ActivityTableData extends CustomerLogForStaff {
  canEdit: boolean;
  categoryString: string;
  contactName: string;
  authorName?: string; // For tagged activities
}

@Component({
  selector: 'app-my-activities',
  templateUrl: './my-activities.component.html',
  styleUrls: ['./my-activities.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    RouterModule,
    UiModule,
    TableColumnsComponent,
    TableHeadersComponent,
    IconButtonComponent
  ],
  providers: [
    NoteCategoryPipe,
    NoteTypePipe,
    DatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyActivitiesComponent implements OnInit, OnDestroy {
  // Data signals
  scheduledActivities = signal<ActivityTableData[]>([]);
  completedActivities = signal<ActivityTableData[]>([]);
  taggedActivities = signal<ActivityTableData[]>([]);
  
  // Loading signals
  scheduledLoading = signal(true);
  completedLoading = signal(true);
  taggedLoading = signal(true);
  
  // Error signals
  scheduledError = signal<string | null>(null);
  completedError = signal<string | null>(null);
  taggedError = signal<string | null>(null);

  // Column definitions for the tables
  scheduledColumns: WritableSignal<Column<ActivityTableData>[]> = signal([
    { field: 'groupName', header: 'Group Name', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: false },
    { field: 'contactName', header: 'Contact', type: 'text', sortable: false, filterable: false },
    { field: 'logType', header: 'Type', type: 'logtype', sortable: false, filterable: false },
    { field: 'categoryString', header: 'Category', type: 'text', sortable: true, filterable: false },
    { field: 'notes', header: 'Note', type: 'html', sortable: false, filterable: false },
    { field: 'dateScheduled', header: 'Date Scheduled', type: 'date', sortable: true, filterable: false }
  ]);

  completedColumns: WritableSignal<Column<ActivityTableData>[]> = signal([
    { field: 'groupName', header: 'Group Name', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: false },
    { field: 'contactName', header: 'Contact', type: 'text', sortable: false, filterable: false },
    { field: 'logType', header: 'Type', type: 'logtype', sortable: false, filterable: false },
    { field: 'categoryString', header: 'Category', type: 'text', sortable: true, filterable: false },
    { field: 'notes', header: 'Note', type: 'html', sortable: false, filterable: false },
    { field: 'dateCompleted', header: 'Date Completed', type: 'date', sortable: true, filterable: false }
  ]);

  taggedColumns: WritableSignal<Column<ActivityTableData>[]> = signal([
    { field: 'groupName', header: 'Group Name', type: 'groupNameWithLink', custIdField: 'custId', sortable: true, filterable: false },
    { field: 'authorName', header: 'Author', type: 'text', sortable: false, filterable: false },
    { field: 'contactName', header: 'Contact', type: 'text', sortable: false, filterable: false },
    { field: 'logType', header: 'Type', type: 'logtype', sortable: false, filterable: false },
    { field: 'categoryString', header: 'Category', type: 'text', sortable: true, filterable: false },
    { field: 'notes', header: 'Note', type: 'html', sortable: false, filterable: false },
    { field: 'dateCompleted', header: 'Date Completed', type: 'date', sortable: true, filterable: false }
  ]);

  subscriptions: Subscription[] = [];

  constructor(
    private notesService: NotesService,
    private noteCategoryPipe: NoteCategoryPipe,
    private noteTypePipe: NoteTypePipe,
    private datePipe: DatePipe,
    private auth: LocalAuthService,
    private dataPadService: DataPadService,
    private messageService: MessageService,
    private errorService: ErrorService,
    public icons: IconService
  ) { }

  ngOnInit() {
    this.loadAllActivities();
    
    // Subscribe to activity updates
    this.subscriptions.push(
      this.notesService.scheduledActivitiesUpdated$.subscribe(() => this.loadScheduledActivities())
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private loadAllActivities() {
    this.loadScheduledActivities();
    this.loadCompletedActivities();
    this.loadTaggedActivities();
  }

  loadScheduledActivities() {
    const staffId = this.auth.currentUser?.newstaffId;
    if (!staffId) return;

    this.scheduledLoading.set(true);
    this.scheduledError.set(null);

    this.notesService.getAllScheduledActivitiesForStaff(staffId).subscribe({
      next: (activities) => {
        this.scheduledActivities.set(this.transformActivities(activities));
        this.scheduledLoading.set(false);
      },
      error: (error) => {
        this.errorService.error('Failed to load scheduled activities', error, this.messageService);
        this.scheduledError.set('Failed to load scheduled activities');
        this.scheduledLoading.set(false);
      }
    });
  }

  loadCompletedActivities() {
    const staffId = this.auth.currentUser?.newstaffId;
    if (!staffId) return;

    this.completedLoading.set(true);
    this.completedError.set(null);

    this.notesService.getAllCompletedActivitiesForStaff(staffId).subscribe({
      next: (activities) => {
        this.completedActivities.set(this.transformActivities(activities));
        this.completedLoading.set(false);
      },
      error: (error) => {
        this.errorService.error('Failed to load completed activities', error, this.messageService);
        this.completedError.set('Failed to load completed activities');
        this.completedLoading.set(false);
      }
    });
  }

  loadTaggedActivities() {
    const staffId = this.auth.currentUser?.newstaffId;
    if (!staffId) return;

    this.taggedLoading.set(true);
    this.taggedError.set(null);

    this.notesService.getAllTaggedActivitiesForStaff(staffId).subscribe({
      next: (activities) => {
        this.taggedActivities.set(this.transformActivities(activities, true));
        this.taggedLoading.set(false);
      },
      error: (error) => {
        this.errorService.error('Failed to load tagged activities', error, this.messageService);
        this.taggedError.set('Failed to load tagged activities');
        this.taggedLoading.set(false);
      }
    });
  }

  private transformActivities(activities: CustomerLogForStaff[], includeAuthor: boolean = false): ActivityTableData[] {
    return activities.map(activity => ({
      ...activity,
      dateScheduled: activity.dateScheduled,
      dateCompleted: activity.dateCompleted,
      groupName: activity.groupName,
      contactName: `${activity.contactFirstName ?? ''} ${activity.contactLastName ?? ''}`.trim() || '(none)',
      authorName: includeAuthor ? (activity as any).authorName : undefined,
      categoryString: this.noteCategoryPipe.transform(activity.category),
      notes: activity.notes ? activity.notes.replaceAll(/[\r\n\t]+/gm, ' ') : '',
      canEdit: this.notesService.canEditNote(this.auth.currentUser?.newstaffId, activity),
      custId: activity.custId,
      // Ensure logType is always a valid number to prevent FontAwesome icon errors
      logType: activity.logType ?? 1 // Default to 'note' if null/undefined
    }));
  }


  addActivity() {
    this.dataPadService.showPanel<EditScheduledActivityComponentInputs>({
      panelName: "editScheduledActivity",
      inputData: {
        tripDetails: [],
        custId: 0,
        contacts: [],
        inputObservables: {
          contacts: of<Contact[]>([]),
          tripDetails: of<TripDetail[]>([])
        }
      }
    });
  }

  editActivity(activity: ActivityTableData) {
    this.notesService.getCustomerDetailsForNoteEdit(activity.custId).subscribe({
      next: (results) => {
        this.dataPadService.showPanel<EditScheduledActivityComponentInputs>({
          panelName: "editScheduledActivity",
          inputData: {
            tripDetails: results.tripDetails,
            custId: activity.custId,
            contacts: results.contacts,
            editActivity: activity
          }
        });
      },
      error: (error) => {
        this.errorService.error('Failed to load customer details', error, this.messageService);
      }
    });
  }

  completeActivity(activity: ActivityTableData) {
    this.notesService.getCustomerDetailsForNoteEdit(activity.custId).subscribe({
      next: (results) => {
        this.dataPadService.showPanel({
          panelName: "completeScheduledActivity",
          inputData: {
            tripDetails: results.tripDetails,
            contacts: results.contacts,
            editActivity: activity,
            custId: activity.custId
          }
        });
      },
      error: (error) => {
        this.errorService.error('Failed to load customer details', error, this.messageService);
      }
    });
  }

  deleteActivityClick(activity: ActivityTableData) {
    this.dataPadService.showPanel({ 
      panelName: "deleteNote", 
      inputData: { note: activity }, 
      outputs: { yesClick: (note: Note) => this.deleteActivity(note) } 
    });
  }

  deleteActivity(note: Note) {
    this.notesService.deleteNote(note.id).subscribe({
      next: result => {
        // Update the appropriate list
        if (this.scheduledActivities().some(a => a.id === note.id)) {
          this.scheduledActivities.update(activities => activities.filter(a => a.id !== note.id));
        } else if (this.completedActivities().some(a => a.id === note.id)) {
          this.completedActivities.update(activities => activities.filter(a => a.id !== note.id));
        } else if (this.taggedActivities().some(a => a.id === note.id)) {
          this.taggedActivities.update(activities => activities.filter(a => a.id !== note.id));
        }
      },
      error: (error) => {
        this.errorService.error('Failed to delete activity', error, this.messageService);
      }
    });
  }
}
